// src/pages/LoginPage.js
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import Button from '../components/common/Button';
import './AuthPages.css'; // Create this CSS file for styling
import backimage from '../assets/images/insurance-professional.jpg';
import logo from "../assets/logo_sanlam_allianz.png";

const LoginPage = () => {
    const [credentials, setCredentials] = useState({
        login: '',
        password: '',
    });

    const { login, isAuthenticated, loading, error, clearError } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();

    const from = location.state?.from?.pathname || '/';

    useEffect(() => {
        if (isAuthenticated) {
            navigate(from, { replace: true });
        }
    }, [isAuthenticated, navigate, from]);

    useEffect(() => {
        clearError();
    }, [clearError]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setCredentials((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        login(credentials);
    };

    const styles = {
        container: {
            minHeight: '100vh',
            display: 'flex',
        },
        leftSide: {
            width: '66.66%',
            backgroundImage: `url(${backimage})`,
            backgroundSize: 'auto 100%',
            backgroundRepeat: 'no-repeat',
            backgroundPosition: 'center',
            backgroundBlendMode: 'overlay',
            position: 'relative',
            overflow: 'hidden',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
        },
        overlay: {
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
        },
        circle: {
            position: 'absolute',
            borderRadius: '50%',
        },
        largeCircle: {
            width: '8rem',
            height: '8rem',
            backgroundColor: 'rgba(15, 106, 235, 0.3)',
        },
        mediumCircle: {
            width: '4rem',
            height: '4rem',
            backgroundColor: 'rgba(3, 32, 71, 0.25)',
        },
        smallCircle: {
            width: '2rem',
            height: '2rem',
            backgroundColor: 'rgba(16, 107, 235, 0.4)',
        },
        content: {
            position: 'relative',
            zIndex: 10,
            maxWidth: '20rem',
            backgroundColor: 'rgba(0, 0, 0, 0.2)',
            backdropFilter: 'blur(10px)',
            borderRadius: '10px',
            padding: '2rem',
        },
        title: {
            color: 'white',
            fontSize: '1.5rem',
            fontWeight: 'bold',
            marginBottom: '1.5rem',
        },
        paragraph: {
            color: 'white',
            fontSize: '1rem',
            lineHeight: '1.5',
        },
        rightSide: {
            width: '33.33%',
            backgroundColor: 'white',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '2rem',
        },
        logoContainer: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginBottom: '2rem',
        },
        logoCircle: {
            width: '2rem',
            height: '2rem',
            backgroundColor: '#1E40AF',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginRight: '0.5rem',
        },
        logoDot: {
            width: '1rem',
            height: '1rem',
            backgroundColor: 'white',
            borderRadius: '50%',
        },
        logoText: {
            color: '#1E40AF',
            fontSize: '1.25rem',
            fontWeight: 'bold',
        },
        logoSubtitle: {
            color: '#1E40AF',
            fontSize: '0.875rem',
        },
        formTitle: {
            color: '#334155',
            fontSize: '1.125rem',
            fontWeight: '600',
            marginBottom: '2rem',
        },
        input: {
            width: '100%',
            padding: '1rem',
            border: '1px solid #D1D5DB',
            borderRadius: '0.5rem',
            fontSize: '1rem',
            marginBottom: '1.5rem',
        },
        button: {
            width: '100%',
            backgroundColor: '#EC4899',
            color: 'white',
            fontWeight: '600',
            padding: '1rem',
            borderRadius: '0.5rem',
            transition: 'background-color 0.2s',
            cursor: 'pointer',
        },
        buttonHover: {
            backgroundColor: '#DB2777',
        },
        checkboxContainer: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            fontSize: '0.875rem',
        },
        checkbox: {
            width: '1rem',
            height: '1rem',
            color: '#1E40AF',
            backgroundColor: '#F3F4F6',
            border: '1px solid #D1D5DB',
            borderRadius: '0.25rem',
            cursor: 'pointer',
        },
        checkboxLabel: {
            color: '#4B5563',
            marginLeft: '0.5rem',
        },
        forgotPassword: {
            color: '#1E40AF',
            textDecoration: 'none',
            cursor: 'pointer',
        },
        formContainer: {
            position: 'relative',
            width: '100%',
            padding: '2rem',
            backgroundColor: '#f9f9f9',
            borderRadius: '10px',
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
            background: 'linear-gradient(45deg, #f0f0f0 25%, transparent 25%) -50px 0, linear-gradient(45deg, #f0f0f0 25%, transparent 25%) 50px 0, linear-gradient(45deg, #f0f0f0 25%, transparent 25%) 0 -50px, linear-gradient(45deg, #f0f0f0 25%, transparent 25%) 0 50px',
            backgroundSize: '100px 100px',
            backgroundPosition: '0 0, 50px 50px',
        },
    };

    return (
        <div style={styles.container}>
            <div style={styles.leftSide}>
                <div style={styles.overlay}>
                    <div style={{ ...styles.circle, ...styles.largeCircle, top: '5rem', left: '5rem' }}></div>
                    <div style={{ ...styles.circle, ...styles.largeCircle, top: '10rem', right: '8rem' }}></div>
                    <div style={{ ...styles.circle, ...styles.largeCircle, bottom: '8rem', left: '4rem' }}></div>
                    <div style={{ ...styles.circle, ...styles.largeCircle, bottom: '5rem', right: '5rem' }}></div>
                    <div style={{ ...styles.circle, ...styles.mediumCircle, top: '8rem', left: '33.33%' }}></div>
                    <div style={{ ...styles.circle, ...styles.mediumCircle, top: '15rem', right: '25%' }}></div>
                    <div style={{ ...styles.circle, ...styles.mediumCircle, bottom: '10rem', left: '50%' }}></div>
                    <div style={{ ...styles.circle, ...styles.smallCircle, top: '4rem', right: '33.33%' }}></div>
                    <div style={{ ...styles.circle, ...styles.smallCircle, top: '20rem', left: '25%' }}></div>
                    <div style={{ ...styles.circle, ...styles.smallCircle, bottom: '12rem', right: '33.33%' }}></div>
                    <div style={{ ...styles.circle, ...styles.smallCircle, top: '50%', left: '2rem' }}></div>
                    <div style={{ ...styles.circle, ...styles.smallCircle, bottom: '20rem', right: '2rem' }}></div>
                </div>
                <div style={styles.content}>
                    <h1 style={styles.title}>L'éthique</h1>
                    <p style={styles.paragraph}>
                        Les collaborateurs de SanlamAllianz sont attachés aux valeurs de respect, de droiture d'esprit et de transparence.
                        Le souci du client final, particulier ou entreprise, est omniprésent dans chaque acte de gestion.
                    </p>
                </div>
            </div>
            <div style={styles.rightSide}>
                <div style={{ maxWidth: '28rem' }}>
                    <div style={styles.logoContainer}>
                        <div>
                            <img src={logo} alt="Logo" />
                        </div>
                        <div>
                            {/* <h2 style={styles.logoText}>Sanlam</h2>
                            <p style={styles.logoSubtitle}>Burkina Faso</p> */}
                        </div>
                    </div>
                    <h3 style={styles.formTitle}>Espace de Connexion</h3>
                    <form  onSubmit={handleSubmit}>
                        {error && <div className="error-message">{error}</div>}
                        <div>
                            <label htmlFor="username" style={{ display: 'none' }}>
                                Nom d'utilisateur
                            </label>
                            <input
                                type="text"
                                id="login"
                                name="login"
                                placeholder="Nom d'utilisateur"
                                value={credentials.login}
                                onChange={handleChange}
                                required
                                style={styles.input}
                            />
                        </div>
                        <div>
                            <label htmlFor="password" style={{ display: 'none' }}>
                                Mot de passe
                            </label>
                            <input
                                type="password"
                                id="password"
                                name="password"
                                placeholder="Mot de passe"
                                value={credentials.password}
                                onChange={handleChange}
                                required
                                style={styles.input}
                            />
                        </div>
                        <Button
                            type="submit"
                            disabled={loading}
                            className="auth-button"
                        >
                            {loading ? 'Connexion en cours...' : 'Se Connecter'}
                        </Button>
                        <div style={styles.checkboxContainer}>
                            <label>
                                <input
                                    type="checkbox"
                                    style={styles.checkbox}
                                />
                                <span style={styles.checkboxlabel}>Se souvenir de moi</span>
                            </label>
                            <a href="#" style={styles.forgotPassword}>
                                Mot de passe oublié?
                            </a>
                        </div>
                    </form>
                </div>
            </div>
        </div>);
};

export default LoginPage;